import { supabase } from '../supabase'

/**
 * ENDLESS word games (Games tab). Same scoring engines as the Weekly
 * dailies, but drawn from a content pool instead of the calendar, and
 * replayable any time. Subscriber-gated server-side — every RPC here
 * checks word_endless_allowed(), so a paywall bypass on the client
 * still can't deal a round.
 *
 * Pass-and-play: one session, every player answers the SAME content in
 * turn, and the winner is decided on SCORE. The client drives whose
 * turn it is; the server scores each player independently.
 *
 * Nothing repeats: the dealers exclude anything in word_seen for the
 * account, in either mode.
 */

// Hand-written Supabase types don't know these RPCs; cast past the
// typed-rpc inference the same way ReportButton does. Bind before
// casting — supabase-js reads `this.rest` inside rpc(), so hoisting the
// bare method into a const detaches it and throws on the first call.
type LooseRpc = (fn: string, args?: Record<string, unknown>) =>
  Promise<{ data: unknown; error: Error | null }>
const rpc = supabase.rpc.bind(supabase) as unknown as LooseRpc

async function call<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await rpc(fn, args)
  if (error) throw error
  return data as T
}

// ── FAKE OUT ────────────────────────────────────────────────────────

export interface FakeoutEndlessServe {
  answered: boolean
  word: string
  position?: number
  total?: number
  defs?: string[]
  /** Present only when this player already answered this position. */
  real_def?: string
  pick?: string
  correct?: boolean
  pts?: number
}

export interface FakeoutEndlessPick {
  correct: boolean
  pts: number
  real_def: string
  word: string
  score: number
  answered: number
  done: boolean
}

export interface FakeoutEndlessResults {
  players: number
  scores: { player: number; score: number; correct: number }[]
  /**
   * One entry per round, in play order. `picks` is every seat's answer
   * for that round, which is what makes the results screen the payoff
   * rather than an answer key: you can see who fell for what. A seat
   * that never answered has a null pick. Older servers omit `picks`
   * entirely, so treat it as optional.
   */
  words: {
    word: string
    real_def: string
    picks?: { player: number; pick: string | null; correct: boolean }[]
  }[]
}

export function startFakeoutEndless(players: number): Promise<string> {
  return call<string>('fakeout_endless_start', { p_players: players })
}

export function serveFakeoutEndless(
  session: string, player: number, position: number,
): Promise<FakeoutEndlessServe> {
  return call<FakeoutEndlessServe>('fakeout_endless_serve', {
    p_session: session, p_player: player, p_position: position,
  })
}

export function pickFakeoutEndless(
  session: string, player: number, position: number, def: string,
): Promise<FakeoutEndlessPick> {
  return call<FakeoutEndlessPick>('fakeout_endless_pick', {
    p_session: session, p_player: player, p_position: position, p_def: def,
  })
}

export function fakeoutEndlessResults(session: string): Promise<FakeoutEndlessResults> {
  return call<FakeoutEndlessResults>('fakeout_endless_results', { p_session: session })
}

// ── CLOSE ENOUGH ────────────────────────────────────────────────────

export interface CloseEndlessState {
  players: number
  player: number
  guesses: number
  hints: number
  solved: boolean
  /** Ended without solving. `secret` is populated in this case too. */
  gaveUp: boolean
  score: number | null
  secret: string | null
  list: { guess: string; rank: number | null; isHint: boolean }[]
  scores: { player: number; score: number | null; guesses: number; solved: boolean; gaveUp: boolean }[]
}

export interface CloseEndlessGuess {
  solved: boolean
  repeat: boolean
  guess?: string
  rank?: number | null
  guesses?: number
  score?: number
  secret?: string
}

export function startCloseEnoughEndless(players: number): Promise<string> {
  return call<string>('closeenough_endless_start', { p_players: players })
}

// ── Co-op rooms: one shared board, no hints ─────────────────────────

export interface CoopGuessRow {
  guess: string
  rank: number | null
  /** Seat index that played it. */
  by: number
  /** Seat display name at read time. */
  name: string
}

export interface CloseCoopState {
  players: number
  player: number
  /** Total guesses the table has made. */
  guesses: number
  solved: boolean
  score: number | null
  /** What the word is worth if solved right now. */
  poolLeft: number
  secret: string | null
  solvedBy: number | null
  list: CoopGuessRow[]
}

export interface CloseCoopGuess {
  solved: boolean
  repeat: boolean
  guess?: string
  rank?: number | null
  score?: number
  secret?: string
  by?: number
}

export function closeEnoughCoopState(
  session: string, player: number,
): Promise<CloseCoopState> {
  return call<CloseCoopState>('closeenough_coop_state', {
    p_session: session, p_player: player,
  })
}

export function closeEnoughCoopGuess(
  session: string, player: number, guess: string,
): Promise<CloseCoopGuess> {
  return call<CloseCoopGuess>('closeenough_coop_guess', {
    p_session: session, p_player: player, p_guess: guess,
  })
}

export function closeEnoughEndlessState(
  session: string, player: number,
): Promise<CloseEndlessState> {
  return call<CloseEndlessState>('closeenough_endless_state', {
    p_session: session, p_player: player,
  })
}

export function closeEnoughEndlessGuess(
  session: string, player: number, guess: string,
): Promise<CloseEndlessGuess> {
  return call<CloseEndlessGuess>('closeenough_endless_guess', {
    p_session: session, p_player: player, p_guess: guess,
  })
}

export function closeEnoughEndlessHint(
  session: string, player: number,
): Promise<{ word: string; rank: number; hints: number; hintsLeft: number }> {
  return call('closeenough_endless_hint', { p_session: session, p_player: player })
}

/**
 * Ends the round unsolved and returns the secret. ENDLESS ONLY — the
 * Weekly daily has no equivalent on purpose, because everyone is playing
 * the same secret that day and revealing it would leak to players who
 * haven't finished.
 */
export function closeEnoughEndlessGiveUp(
  session: string, player: number,
): Promise<{ gaveUp: boolean; secret: string; score: number }> {
  return call('closeenough_endless_give_up', { p_session: session, p_player: player })
}
